import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Radar, Plus, SkipForward, ThumbsDown, Clock, Car, SquareParking, Ban, Phone, Mail, Globe, MapPin, Eye,
  ExternalLink, Star, User, Users, RefreshCw,
} from "lucide-react";
import { Pill, PotentialPill, btnGhost, btnPrimary, card, label, selectBase, streetViewUrl } from "@/components/crm/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/crm`;

// Vending filters Dan picked: who is there (shift workers, people waiting), how big it is, and hiding dead ends
const CROWDS = [
  { key: "shift", label: "Shift work", hint: "Open early, late, weekends or 24/7" },
  { key: "waiting", label: "Waiting rooms", hint: "Car service, urgent care, laundromats" },
];
const SHIFT_LABEL = { "24/7": "Open 24/7", early: "Opens before 6 AM", late: "Open past 10 PM", weekend: "Open weekends" };
const DEAD_END_LABEL = { "national chain": "National chain", "sells food": "Sells food", "public school": "Public school" };

const chip = (on) => `rounded-full border px-3 py-1.5 font-mono text-[11px] tracking-[0.1em] ${on
  ? "border-vortex-cyan bg-vortex-cyan text-vortex-ink" : "border-white/15 text-slate-300 hover:border-vortex-cyan/60"}`;

const LeadSweep = ({ auth, fail, categories, onAdded }) => {
  const [status, setStatus] = useState(null);
  const [filters, setFilters] = useState({ crowd: [], big: false, hide_dead_ends: true, category: "" });
  const [queue, setQueue] = useState(null);
  const [details, setDetails] = useState({});
  const [busy, setBusy] = useState("");
  const running = ["searching", "sizing"].includes(status?.state?.status);
  const catLabel = (k) => categories.find((c) => c.key === k)?.label || k;

  const loadStatus = useCallback(() => axios.get(`${API}/sweep`, auth)
    .then((res) => setStatus(res.data)).catch((err) => fail(err, "Could not load the sweep.")), [auth, fail]);

  const loadQueue = useCallback(() => {
    const params = { crowd: filters.crowd.join(","), big: filters.big, hide_dead_ends: filters.hide_dead_ends, limit: 50 };
    if (filters.category) params.category = filters.category;
    return axios.get(`${API}/sweep/queue`, { ...auth, params })
      .then((res) => setQueue(res.data)).catch((err) => fail(err, "Could not load the review queue."));
  }, [auth, fail, filters]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  // While a sweep runs, check on it every few seconds and refresh the queue as places come in
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => { loadStatus(); loadQueue(); }, 4000);
    return () => clearInterval(t);
  }, [running, loadStatus, loadQueue]);

  const current = queue?.items?.[0];

  // Contacts and hours load on their own for the place on screen, so there is nothing to click
  useEffect(() => {
    if (!current || details[current.id]) return;
    setDetails((d) => ({ ...d, [current.id]: { loading: true } }));
    axios.post(`${API}/search/details`, { source: current.source, source_ref: current.source_ref,
      website: current.website, category: current.category || null }, auth)
      .then((res) => setDetails((d) => ({ ...d, [current.id]: res.data })))
      .catch(() => setDetails((d) => ({ ...d, [current.id]: { errors: ["Couldn't load details."] } })));
  }, [current, details, auth]);

  const startSweep = async () => {
    setBusy("sweep");
    try {
      const res = await axios.post(`${API}/sweep`, {}, auth);
      setStatus(res.data);
      toast.success("Sweep started. New places show up below as they're found.");
    } catch (err) {
      fail(err, "Could not start the sweep.");
    } finally {
      setBusy("");
    }
  };

  const withDetails = (r) => {
    const d = details[r.id] || {};
    const dm = d.people?.find((p) => p.decision_maker);
    return { company: r.company, address: r.address || "", city: r.city || "", state: r.state || "",
      postal_code: r.postal_code || "", lat: r.lat, lng: r.lng, category: r.category || null, types: r.types || [],
      source: r.source, source_ref: r.source_ref, email: dm?.email || r.email || d.emails?.[0] || "",
      phone: r.phone || d.phone || d.phones?.[0] || "", contact_name: dm?.name || "", title: dm?.title || "",
      contact_phone: dm?.phone || "", website: r.website || d.website || "", notes: d.summary || "",
      reviews: r.reviews ?? d.reviews ?? null, hours_per_week: r.hours_per_week ?? d.hours_per_week ?? null,
      style_hint: d.style_hint || "" };
  };

  const decide = useCallback(async (action) => {
    if (!current || busy) return;
    setBusy(action);
    try {
      await axios.post(`${API}/sweep/queue/${current.id}`, { action, lead: action === "skip" ? null : withDetails(current) }, auth);
      const rest = queue.items.slice(1);
      setQueue((q) => ({ ...q, items: rest, total: q.total - 1 }));
      if (action === "add") { toast.success(`${current.company} added to your leads.`); onAdded(); }
      if (rest.length < 5) loadQueue();
      loadStatus();
    } catch (err) {
      fail(err, "Could not save that.");
    } finally {
      setBusy("");
    }
  // withDetails reads the latest details; listing it would re-bind on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, busy, auth, queue, details, onAdded, loadQueue, loadStatus, fail]);

  // Keyboard: A add, S skip, N not a fit (ignored while typing in a field)
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.metaKey || e.ctrlKey || e.altKey) return;
      const action = { a: "add", s: "skip", n: "not_fit" }[e.key.toLowerCase()];
      if (action && current) { e.preventDefault(); decide(action); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [decide, current]);

  const toggleCrowd = (key) => setFilters((f) => ({
    ...f, crowd: f.crowd.includes(key) ? f.crowd.filter((c) => c !== key) : [...f.crowd, key] }));

  const usage = status?.usage;
  const sweepCats = useMemo(() => categories.filter((c) => status?.categories?.includes(c.key)), [categories, status]);
  const d = current ? details[current.id] : null;

  return (
    <div className={`${card} mt-10 p-6`} data-testid="crm-sweep">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-vortex-cyan uppercase"><Radar size={13} /> Sweep my area</p>
          <p className="mt-3 text-sm text-slate-300">
            Searches every good-fit business type around {status?.centers?.length || 8} towns ({status?.centers?.join(", ")}),
            {" "}{status?.radius_miles || 6} mi each, then lines up the new places best first. Decide each one once and it never comes back.
          </p>
          {usage && (
            <p className="mt-2 text-xs text-slate-500" data-testid="crm-sweep-usage">
              {usage.calls} of {usage.cap} Google searches used this month · a full sweep uses about {status.calls_per_sweep}
              {status.last_run_at && ` · last sweep ${new Date(status.last_run_at).toLocaleDateString()}`}
            </p>
          )}
        </div>
        <button type="button" className={btnPrimary} onClick={startSweep} disabled={running || busy === "sweep"} data-testid="crm-sweep-start">
          <Radar size={14} /> {running ? "Sweeping..." : "Run sweep"}
        </button>
      </div>

      {running && (
        <div className="mt-5" data-testid="crm-sweep-progress">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div className="h-full bg-vortex-cyan transition-all" style={{ width: `${status.state.progress || 0}%` }} />
          </div>
          <p className="mt-2 text-xs text-slate-400">
            {status.state.status === "searching" ? "Searching Google"
              : `Checking parking lots for the top ${status.state.to_size || ""} places (${status.state.sized || 0} done)`} · {status.state.found || 0} new places so far. You can start reviewing below now.
          </p>
        </div>
      )}
      {!running && status?.state?.error && <p className="mt-4 text-xs text-amber-300">{status.state.error}</p>}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="crm-sweep-filters">
        {CROWDS.map((c) => (
          <button key={c.key} type="button" title={c.hint} className={chip(filters.crowd.includes(c.key))} onClick={() => toggleCrowd(c.key)}>{c.label}</button>
        ))}
        <button type="button" title="50+ parking spaces, 50+ people or 100+ Google reviews" className={chip(filters.big)}
          onClick={() => setFilters((f) => ({ ...f, big: !f.big }))}>Big places only</button>
        <button type="button" title="National chains, places that sell food, public schools" className={chip(!filters.hide_dead_ends)}
          onClick={() => setFilters((f) => ({ ...f, hide_dead_ends: !f.hide_dead_ends }))}>
          Show dead ends{queue?.dead_ends ? ` (${queue.dead_ends})` : ""}
        </button>
        <select aria-label="Business type" className={`${selectBase} w-48`} value={filters.category}
          onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
          <option value="">All types</option>
          {sweepCats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        {queue && <span className="ml-auto text-xs text-slate-500" data-testid="crm-sweep-count">{queue.total} to review{queue.total !== queue.queued ? ` (of ${queue.queued} in the queue)` : ""}</span>}
      </div>

      {/* One place at a time */}
      {!queue ? <p className="mt-6 text-sm text-slate-500">Loading...</p> : !current ? (
        <p className="mt-6 text-sm text-slate-500" data-testid="crm-sweep-empty">
          {queue.queued ? "Nothing matches these filters. Turn one off to see more." : status?.state?.status === "never"
            ? "Run your first sweep to fill this list." : "All caught up. Run a sweep again later to find new places."}
        </p>
      ) : (
        <div className="mt-6 border border-white/10 bg-vortex-ink/40 p-5" data-testid="crm-sweep-card">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-lg font-bold text-white">{current.company}</span>
            <PotentialPill potential={current.potential} />
            <Pill className="border-vortex-cyan/40 text-vortex-cyan">{catLabel(current.category)}</Pill>
            {current.dead_end && <Pill className="border-red-400/50 text-red-300"><Ban size={10} className="mr-1" />{DEAD_END_LABEL[current.dead_end]}</Pill>}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
            {current.type_label && <span>{current.type_label}</span>}
            <span>{current.address || "No address listed"}</span>
            {current.base_miles != null && <span>{current.base_miles.toFixed(1)} mi from your route start</span>}
            {current.rating != null && <span className="flex items-center gap-1"><Star size={11} className="text-amber-300" />{current.rating.toFixed(1)}{current.reviews ? ` (${current.reviews} reviews)` : ""}</span>}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(current.shift || []).map((s) => <Pill key={s} className="border-emerald-400/50 text-emerald-300"><Clock size={10} className="mr-1" />{SHIFT_LABEL[s]}</Pill>)}
            {current.waiting_room && <Pill className="border-emerald-400/50 text-emerald-300"><Car size={10} className="mr-1" />Waiting room</Pill>}
            {current.parking_spaces && <Pill className="border-emerald-400/50 text-emerald-300"><SquareParking size={10} className="mr-1" />About {current.parking_spaces} parking spaces</Pill>}
            {current.employees && <Pill className="border-emerald-400/50 text-emerald-300"><Users size={10} className="mr-1" />About {current.employees} people</Pill>}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 text-xs text-slate-300 sm:grid-cols-2">
            <div className="space-y-2">
              {current.phone && <p className="flex items-center gap-1"><Phone size={11} className="text-vortex-cyan" />{current.phone}</p>}
              {current.website && <p className="flex items-center gap-1"><Globe size={11} className="text-vortex-cyan" />{current.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</p>}
              {!d || d.loading ? <p className="text-slate-500">Looking up contacts on their website...</p> : (
                <>
                  {d.summary && <p className="text-slate-400">{d.summary}</p>}
                  {d.people?.slice(0, 3).map((p) => (
                    <p key={p.name} className="flex flex-wrap items-center gap-2">
                      <User size={11} className="text-vortex-cyan" />{p.name}{p.title ? `, ${p.title}` : ""}
                      {p.decision_maker && <Pill className="border-emerald-400/50 text-emerald-300">Decision-maker</Pill>}
                      {p.email && <span className="text-slate-400">{p.email}</span>}
                    </p>
                  ))}
                  {d.emails?.slice(0, 3).map((em) => <p key={em} className="flex items-center gap-1"><Mail size={11} className="text-vortex-cyan" />{em}</p>)}
                  {!d.emails?.length && !d.people?.length && <p className="text-slate-500">{current.website ? "No emails found on their website." : "No website listed."}</p>}
                </>
              )}
              <div className="flex flex-wrap gap-4 pt-1">
                {current.maps_url && <a href={current.maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-vortex-cyan hover:underline"><MapPin size={11} /> Google Maps</a>}
                {streetViewUrl(current) && <a href={streetViewUrl(current)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-vortex-cyan hover:underline"><Eye size={11} /> Street View</a>}
                {current.website && <a href={current.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-vortex-cyan hover:underline"><ExternalLink size={11} /> Website</a>}
              </div>
            </div>
            <div>
              <p className={label}>Why this score</p>
              <ul className="mt-1 space-y-0.5 text-slate-400">
                {current.potential.parts.map((p) => <li key={p.key}>{p.label}: {p.points}/{p.max} · {p.why}</li>)}
              </ul>
              {d?.hours?.length > 0 && (
                <>
                  <p className={`${label} mt-3`}>Hours</p>
                  <ul className="mt-1 space-y-0.5 text-slate-400">{d.hours.map((h) => <li key={h}>{h}</li>)}</ul>
                </>
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button type="button" className={btnPrimary} onClick={() => decide("add")} disabled={!!busy} data-testid="crm-sweep-add">
              <Plus size={14} /> Add to leads <kbd className="opacity-60">A</kbd>
            </button>
            <button type="button" className={btnGhost} onClick={() => decide("skip")} disabled={!!busy} data-testid="crm-sweep-skip">
              <SkipForward size={13} /> Skip <kbd className="opacity-60">S</kbd>
            </button>
            <button type="button" className={btnGhost} onClick={() => decide("not_fit")} disabled={!!busy} data-testid="crm-sweep-notfit">
              <ThumbsDown size={13} /> Not a fit <kbd className="opacity-60">N</kbd>
            </button>
            <button type="button" className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-vortex-cyan" onClick={loadQueue} aria-label="Refresh queue">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">Skip hides it from the sweep only. Not a fit also hides it from regular searches.</p>
        </div>
      )}
    </div>
  );
};

export default LeadSweep;
